import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';

import { REPORT_ID } from 'consts';
import config from 'config';
import {
  deleteReport,
  deselectDocument,
  getReportingDcouments,
  getReportList,
  hideModal,
  selectIsReportAdmin,
  selectReportList,
  selectSelectedGroup,
  showModal,
} from 'stores';
import * as utils from 'utils';

import { ReportingGroupView } from './ReportingGroup.view';

const Reporting = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const brand = useSelector((state) => state.ui.brand);
  const reportingList = useSelector(selectReportList);
  const selectedGroup = useSelector(selectSelectedGroup);
  const isReportAdmin = useSelector(selectIsReportAdmin);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const groupTitle = selectedGroup.name;
  const reportGroupId = id;
  const documents = useSelector((state) => state.document.documents);
  const folders = useSelector((state) => state.document.folders);

  useEffect(() => {
    dispatch(getReportList(id));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => {
      let isSubscribed = true;
      if (!reportGroupId) return null;
      if (isSubscribed) {
        dispatch(getReportingDcouments(reportGroupId));
        return () => dispatch(deselectDocument());
      }
      return () => {
        isSubscribed = false;
      };
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(
    () => {
      setBreadcrumbs([
        {
          name: 'report-group',
          label: utils.string.t('reporting.title'),
          link: `${config.routes.reporting.root}`,
        },
        {
          name: 'report',
          label: groupTitle || '',
          link: `${config.routes.reporting.root}/${id}`,
          active: false,
        },
      ]);
    },
    [groupTitle] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const filterReportngList = reportingList.items;

  const setActiveStep = (prevBredcrumbs) =>
    prevBredcrumbs.map((item) => {
      const result = { ...item };
      if (item.name === 'report' && history.location.pathname.includes(`reporting/${id}`)) {
        result.active = true;
      } else {
        result.active = history.location.pathname === item.link;
      }
      return result;
    });

  const handleOpenReport = useCallback(
    (report) => {
      const handleCloseReport = () => {
        const search = new URLSearchParams(location.search);
        search.delete(REPORT_ID);
        history.replace({ search: search.toString() });
      };

      dispatch(
        showModal({
          component: 'REPORT',
          props: {
            componentProps: {
              report,
              cancelHandler: handleCloseReport,
            },
            maxWidth: 'xl',
            fullScreen: true,
            fullWidth: true,
            title: report.title,
            callback: handleCloseReport,
          },
        })
      );
    },
    [dispatch, history, location]
  );

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const reportId = search.get(REPORT_ID);

    if (reportId) {
      const report = reportingList.items.find((item) => `${item.id}` === reportId);

      if (report) {
        handleOpenReport(report);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportingList, dispatch]);

  const handleClickRow = (report) => () => {
    const search = new URLSearchParams(location.search);
    search.set(REPORT_ID, report.id);
    history.replace({ search: search.toString() });

    handleOpenReport(report);
  };

  const handleEdit = (popoverData) => {
    dispatch(
      showModal({
        component: 'ADD_EDIT_REPORT',
        props: {
          fullWidth: true,
          maxWidth: 'md',
          title: utils.string.t('reporting.editReport'),
          componentProps: {
            ...popoverData,
            report: popoverData,
          },
        },
      })
    );
  };
  const handleAdd = () => {
    const newPopoverData = { groupId: id };
    dispatch(
      showModal({
        component: 'ADD_EDIT_REPORT',
        props: {
          fullWidth: true,
          maxWidth: 'md',
          title: utils.string.t('reporting.addReport'),
          componentProps: {
            ...newPopoverData,
            report: newPopoverData,
          },
        },
      })
    );
  };
  const handleDelete = (popoverData) => {
    const submitHandler = () => {
      dispatch(deleteReport(popoverData?.id));
    };

    dispatch(
      showModal({
        component: 'CONFIRM_DELETE',
        props: {
          title: utils.string.t('reporting.deleteReport'),
          subtitle: popoverData.title,
          fullWidth: true,
          maxWidth: 'xs',
          disableAutoFocus: true,
          componentProps: {
            submitHandler,
          },
        },
      })
    );
  };

  const uploadNew = () => {
    dispatch(
      showModal({
        component: 'DOCUMENT_UPLOAD',
        props: {
          title: 'documents.upload.modal.title',
          fullWidth: true,
          maxWidth: 'sm',
          disableAutoFocus: true,
          componentProps: {
            reportGroupId,
            isFolderCreatable: true,
          },
        },
      })
    );
  };

  const uploadBDX = () => {
    dispatch(
      showModal({
        component: 'UPLOAD_BDX',
        props: {
          title: 'documents.upload.bdx.title',
          fullWidth: true,
          maxWidth: 'xl',
          disableAutoFocus: true,
          enableFullScreen: true,
          disableBackdropClick: true,
          componentProps: {
            cancelHandler: () => dispatch(hideModal('UPLOAD_BDX')),
            sanctionsCheckOnly: true,
          },
        },
      })
    );
  };
  return (
    <>
      <Helmet>
        <title>{`${groupTitle} - ${groupTitle} - ${utils.app.getAppName(brand)}`}</title>
      </Helmet>
      <ReportingGroupView
        isReportAdmin={isReportAdmin}
        groupTitle={groupTitle || ''}
        groupId={id}
        list={filterReportngList}
        handleClickRow={handleClickRow}
        handleAdd={handleAdd}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        breadcrumbs={setActiveStep(breadcrumbs) || []}
        uploadNew={uploadNew}
        uploadBDX={uploadBDX}
        documents={documents}
        folders={folders}
      />
    </>
  );
};

export default Reporting;
